import { useEffect, useRef, useState } from "react"
import Cookies from "js-cookie"
import "./chatbot.css"
import ReactMarkdown from "react-markdown"
import { translations } from "../../../utils/Translation/staticTranslation"

/**
 * Append the auth token so the backend can associate the session with the
 * logged-in user (name/email). Browsers can't set custom headers on a
 * WebSocket, so the token goes in the query string per the backend spec.
 */
const buildWebSocketUrl = () => {
  const rawBase = process.env.REACT_APP_WEBSOCKET_URL || ""
  // Backend route is "ws/chatbot/" (trailing slash required) — normalize so a
  // missing slash in the env doesn't cause "No route found for path 'ws/chatbot'".
  const path = rawBase.split("?")[0]
  const base = path.endsWith("/") ? path : `${path}/`

  const params = new URLSearchParams()

  // Identify this client as the merchant dashboard so the backend can scope
  // responses/data to merchant-related topics.
  params.set("type", "merchant")

  const lang = localStorage.getItem("language") === "ar" ? "ar" : "en"
  params.set("lang", lang)

  const token = Cookies.get("token")
  if (token) params.set("token", token)

  return `${base}?${params.toString()}`
}

export default function ChatBot() {
  const chatContentRef = useRef(null)
  const dragRef = useRef(null)

  // Chat state
  const [message, setMessage] = useState("")
  const [chatMessages, setChatMessages] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [socket, setSocket] = useState(null)
  const [isSocketConnected, setIsSocketConnected] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isDisconnected, setIsDisconnected] = useState(false)

  // Dragging state
  const [isDragging, setIsDragging] = useState(false)
  const [dragData, setDragData] = useState({
    startPos: { x: 0, y: 0 },
    offset: { x: 0, y: 0 },
    hasMoved: false,
  })
  const [position, setPosition] = useState(() => {
    const savedPosition = localStorage.getItem("chatbot-position")
    if (savedPosition) {
      return JSON.parse(savedPosition)
    }
    return { x: window.innerWidth - 100, y: window.innerHeight - 100 }
  })

  // Initialize WebSocket connection
  const initializeWebSocket = () => {
    setIsDisconnected(false)

    const newSocket = new WebSocket(buildWebSocketUrl())

    newSocket.onopen = () => {
      console.log("WebSocket connected")
      setIsSocketConnected(true)
      setIsDisconnected(false)
    }

    newSocket.onmessage = (event) => {
      const data = JSON.parse(event.data)

      // "typing" → the bot is thinking; show loading dots only, no bubble.
      if (data.type === "typing") {
        setLoading(true)
        return
      }

      // Control frames (e.g. "session") carry no chat text — ignore them so
      // they don't render as an empty bubble with just the avatar.
      if (data.type === "session" || !data.message) {
        return
      }

      setChatMessages((prev) => [
        ...prev,
        {
          from: "bot",
          text: data.message,
          quickReplies: data.quick_replies || null,
          channels: data.channels || null,
          supportOptions: data.support_options || null,
          whatsappLink: data.whatsapp_link || null,
        },
      ])
      setLoading(false)
    }

    newSocket.onerror = (error) => {
      console.error("WebSocket error", error)
      setIsDisconnected(true)
    }

    newSocket.onclose = () => {
      console.log("WebSocket disconnected")
      setIsSocketConnected(false)
      setIsDisconnected(true)
    }

    setSocket(newSocket)
    return newSocket
  }

  // WebSocket connection effect
  useEffect(() => {
    if (isOpen) {
      const newSocket = initializeWebSocket()
      return () => {
        newSocket.close()
        setSocket(null)
        setIsSocketConnected(false)
      }
    }
  }, [isOpen])

  // Drag and Drop Logic
  const handleStart = (clientX, clientY, isTouch = false) => {
    const rect = dragRef.current?.getBoundingClientRect()
    if (!rect) return

    setDragData({
      startPos: { x: clientX, y: clientY },
      offset: {
        x: clientX - rect.left,
        y: clientY - rect.top,
      },
      hasMoved: false,
    })
  }

  const handleMove = (clientX, clientY) => {
    if (!dragData.startPos.x && !dragData.startPos.y) return

    const deltaX = Math.abs(clientX - dragData.startPos.x)
    const deltaY = Math.abs(clientY - dragData.startPos.y)
    const threshold = 8 // pixels to start drag

    // Start dragging if moved enough
    if (!isDragging && (deltaX > threshold || deltaY > threshold)) {
      setIsDragging(true)
      setDragData((prev) => ({ ...prev, hasMoved: true }))
    }

    // Update position if dragging
    if (isDragging || deltaX > threshold || deltaY > threshold) {
      const newX = clientX - dragData.offset.x
      const newY = clientY - dragData.offset.y

      // Constrain to viewport
      const maxX = window.innerWidth - 80
      const maxY = window.innerHeight - 80

      const constrainedX = Math.max(0, Math.min(newX, maxX))
      const constrainedY = Math.max(0, Math.min(newY, maxY))

      setPosition({ x: constrainedX, y: constrainedY })
    }
  }

  const handleEnd = () => {
    if (isDragging) {
      // Save position
      localStorage.setItem("chatbot-position", JSON.stringify(position))
    }

    // Reset drag state after delay to prevent click
    setTimeout(() => {
      setIsDragging(false)
      setDragData({
        startPos: { x: 0, y: 0 },
        offset: { x: 0, y: 0 },
        hasMoved: false,
      })
    }, 50)
  }

  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault()
    handleStart(e.clientX, e.clientY)
  }

  // Touch events
  const handleTouchStart = (e) => {
    e.preventDefault()
    const touch = e.touches[0]
    handleStart(touch.clientX, touch.clientY, true)
  }

  // Global event listeners for drag
  useEffect(() => {
    const handleMouseMove = (e) => {
      e.preventDefault()
      handleMove(e.clientX, e.clientY)
    }

    const handleMouseUp = (e) => {
      handleEnd()
    }

    const handleTouchMove = (e) => {
      if (isDragging || dragData.hasMoved) {
        e.preventDefault()
      }
      const touch = e.touches[0]
      if (touch) {
        handleMove(touch.clientX, touch.clientY)
      }
    }

    const handleTouchEnd = (e) => {
      handleEnd()
    }

    // Add global listeners
    if (dragData.startPos.x || dragData.startPos.y || isDragging) {
      document.addEventListener("mousemove", handleMouseMove, {
        passive: false,
      })
      document.addEventListener("mouseup", handleMouseUp)
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      })
      document.addEventListener("touchend", handleTouchEnd)
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      document.removeEventListener("touchmove", handleTouchMove)
      document.removeEventListener("touchend", handleTouchEnd)
    }
  }, [dragData, isDragging, position])

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const maxX = window.innerWidth - 80
      const maxY = window.innerHeight - 80

      setPosition((prev) => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY)),
      }))
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Handle button click
  const handleButtonClick = (e) => {
    // Only open chat if we didn't drag
    if (!dragData.hasMoved && !isDragging) {
      setIsOpen(!isOpen)
    }
  }

  // Chat functions
  const handleClose = () => {
    setIsOpen(false)
    setSocket(null)
    setIsSocketConnected(false)
    setIsDisconnected(false)
  }

  const handleSend = () => {
    if (message.trim() && message.length <= 200) {
      const payload = JSON.stringify({ message })

      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(payload)
        setLoading(true)
      } else {
        console.warn("WebSocket is not open.")
        setIsDisconnected(true)
      }

      setChatMessages((prev) => [...prev, { from: "user", text: message }])
      setMessage("")
    }
  }

  // Send a preset option (quick reply / channel choice) as if the user typed it.
  const handleQuickReply = (value) => {
    const label = typeof value === "string" ? value : value?.label || ""
    const payload =
      typeof value === "string"
        ? value
        : value?.id || value?.value || value?.label || ""
    if (!label.trim()) return

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ message: payload }))
      setLoading(true)
    } else {
      setIsDisconnected(true)
    }
    setChatMessages((prev) => [...prev, { from: "user", text: label }])
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSend()
    }
  }

  const scrollToBottom = () => {
    if (chatContentRef.current) {
      chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [chatMessages, loading])

  const handleReconnect = () => {
    if (isOpen && isDisconnected) {
      initializeWebSocket()
    }
  }

  const lang = localStorage.getItem("language") || "ar"
  const chatbot = translations[lang]?.chatbot

  return (
    <>
      {/* Draggable Chat Icon */}
      <div
        ref={dragRef}
        className={`open-chat-container ${isDragging ? "dragging" : ""}`}
        style={{
          position: "fixed",
          left: `${position.x}px`,
          top: `${position.y}px`,
          zIndex: isDragging ? 9999 : 1000,
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          touchAction: "none",
          transition: isDragging ? "none" : "all 0.2s ease",
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="open-chat-button">
          <button
            onClick={handleButtonClick}
            className="open-chat-button_2"
            style={{
              pointerEvents: dragData.hasMoved ? "none" : "auto",
            }}
          >
            <img
              src="/chatbot/chatbot.svg"
              alt=""
              width={32}
              height={32}
              draggable={false}
            />
          </button>
        </div>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <>
          <div className="chat-container" onClick={handleClose}></div>
          <div
            className="chat-content-box"
            style={{
              position: "fixed",
              left:
                position.x > window.innerWidth / 2
                  ? `${Math.max(20, position.x - 400)}px`
                  : `${Math.min(window.innerWidth - 420, position.x + 100)}px`,
              top: `${Math.max(
                20,
                Math.min(window.innerHeight - 570, position.y - 50)
              )}px`,
            }}
          >
            <div className="chat-box">
              <div className="chat-header">
                <div className="header-title">
                  <div className="bot-name">
                    <img
                      src="/logo/logowhite.svg"
                      alt=""
                      width={97}
                      height={23}
                    />
                  </div>
                </div>
                <button onClick={handleClose} className="close-button">
                  <img src="/Storecomments/cross.svg" alt="" />
                </button>
              </div>

              <div className="chat-content" ref={chatContentRef}>
                {chatMessages
                  .filter((msg) => {
                    // Never render an empty bubble (e.g. a stray control frame).
                    if (msg.from === "user") return !!(msg.text && msg.text.trim())
                    return !!(
                      (msg.text && msg.text.trim()) ||
                      msg.quickReplies?.length ||
                      msg.channels?.length ||
                      msg.supportOptions ||
                      msg.whatsappLink
                    )
                  })
                  .map((msg, index) => (
                    <div
                      key={index}
                      className={
                        msg.from === "user" ? "message user" : "message bot"
                      }
                    >
                      {msg.from === "bot" && (
                        <div className="chat-logo">
                          <img src="/logo/chatbotLogo.svg" alt="" />
                        </div>
                      )}
                      <ReactMarkdown>{msg.text}</ReactMarkdown>

                      {/* Option pickers (quick replies / channel choice) */}
                      {msg.from === "bot" &&
                        (msg.quickReplies?.length ||
                          msg.channels?.length ||
                          msg.supportOptions) && (
                          <div className="quick-replies">
                            {(msg.quickReplies || []).map((qr, i) => (
                              <button
                                key={`qr-${i}`}
                                className="quick-reply-btn"
                                onClick={() => handleQuickReply(qr)}
                              >
                                {typeof qr === "string" ? qr : qr?.label}
                              </button>
                            ))}
                            {(msg.channels || []).map((ch, i) => (
                              <button
                                key={`ch-${i}`}
                                className="quick-reply-btn"
                                onClick={() => handleQuickReply(ch)}
                              >
                                {ch?.label}
                                {ch?.note ? ` · ${ch.note}` : ""}
                              </button>
                            ))}
                            {msg.supportOptions?.email && (
                              <a
                                className="quick-reply-btn"
                                href={msg.supportOptions.email.link}
                              >
                                {msg.supportOptions.email.label}
                              </a>
                            )}
                            {msg.supportOptions?.whatsapp && (
                              <a
                                className="quick-reply-btn"
                                href={msg.supportOptions.whatsapp.link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {msg.supportOptions.whatsapp.label}
                              </a>
                            )}
                          </div>
                        )}

                      {/* Confirmed WhatsApp link */}
                      {msg.from === "bot" && msg.whatsappLink && (
                        <div className="quick-replies">
                          <a
                            className="quick-reply-btn"
                            href={msg.whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {lang === "ar" ? "افتح واتساب" : "Open WhatsApp"}
                          </a>
                        </div>
                      )}
                    </div>
                  ))}

                {isSocketConnected && loading && (
                  <div
                    className="message d-flex text-start"
                    style={{ alignSelf: "start" }}
                  >
                    <div className="dot-loading"></div>
                    <div className="dot-loading"></div>
                    <div className="dot-loading"></div>
                    <div className="dot-loading"></div>
                  </div>
                )}

                {isDisconnected && (
                  <div className="connection-status disconnected">
                    <button
                      onClick={handleReconnect}
                      className="reconnect-button"
                    >
                      {chatbot?.connection_lost}
                    </button>
                  </div>
                )}

                {/* "connecting…" until the first bot message actually arrives. */}
                {chatMessages.length === 0 && !isDisconnected && (
                  <div className="connection-status connecting">
                    <p className="mb-0">{chatbot?.connecting_to_support}</p>
                  </div>
                )}
              </div>

              <div className="message-input-container">
                {isSocketConnected && chatMessages.length > 0 && (
                  <div>
                    <p className="character-count">{message.length}/200</p>

                    <div className="input-wrapper-box">
                      <input
                        type="text"
                        placeholder={chatbot?.message_placeholder}
                        className={`message-input ${
                          lang === "ar" ? "ar" : "en"
                        }`}
                        value={message}
                        onChange={(e) => {
                          if (e.target.value.length <= 200) {
                            setMessage(e.target.value)
                          }
                        }}
                        maxLength={200}
                        onKeyDown={handleKeyPress}
                        disabled={!isSocketConnected || isDisconnected}
                      />
                      <button
                        onClick={handleSend}
                        className="send-button"
                        style={{
                          left: lang === "ar" && "10px",
                          right: lang === "en" && "10px",
                          transform: lang === "ar" ? "rotateY(180deg)" : "none",
                        }}
                        disabled={
                          !message.trim() ||
                          !isSocketConnected ||
                          isDisconnected
                        }
                      >
                        <img src="/chatbot/sendIcon.svg" alt="Send" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
